import {
    Assessment,
    Celebration,
    CheckCircle,
    CloudUpload,
    Psychology,
    Schedule,
    Visibility,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect } from 'react';

interface WelcomeStepProps {
    firstName: string;
    companyName: string;
    onNext: () => void;
    setAgentMessage: (message: string) => void;
}

const ONBOARDING_STEPS = [
    {
        icon: <CloudUpload />,
        title: 'CV Upload',
        description: 'Laden Sie Ihren Lebenslauf hoch',
        duration: '2 Min',
    },
    {
        icon: <Assessment />,
        title: 'KI-Analyse',
        description: 'Automatische Erkennung Ihrer Fähigkeiten',
        duration: '3 Min',
    },
    {
        icon: <Visibility />,
        title: 'Überprüfung',
        description: 'Bewerten und ergänzen Sie Ihre Daten',
        duration: '5 Min',
    },
    {
        icon: <Celebration />,
        title: 'Abschluss',
        description: 'Profil erstellen und Team benachrichtigen',
        duration: '1 Min',
    },
];

export default function WelcomeStep({ firstName, companyName, onNext, setAgentMessage }: WelcomeStepProps) {
    const theme = useTheme();

    useEffect(() => {
        setAgentMessage(
            `Hallo ${firstName}! Willkommen bei ${companyName}. Ich bin hier, um Sie durch Ihr Onboarding zu führen. Gemeinsam erstellen wir Ihr perfektes Profil in nur wenigen Schritten!`,
        );
    }, [firstName, companyName, setAgentMessage]);

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {/* Welcome Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    Willkommen, {firstName}!
                </Typography>

                <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                    Schön, dass Sie bei {companyName} sind
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
                    Lassen Sie uns gemeinsam Ihr Profil erstellen. Unser KI-Assistent wird Sie durch jeden Schritt
                    begleiten und dabei helfen, Ihre Fähigkeiten und Erfahrungen optimal zu präsentieren.
                </Typography>

                {/* Action Button */}
                <Button
                    variant="contained"
                    size="large"
                    onClick={onNext}
                    sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
                        },
                        transition: 'all 0.3s ease',
                        mb: 1,
                    }}
                >
                    Onboarding starten
                </Button>

                <Typography variant="body2" color="text.secondary">
                    Du kannst jederzeit pausieren und später fortfahren
                </Typography>
            </Box>

            {/* Process Overview */}
            <Card
                elevation={0}
                sx={{
                    mb: 4,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    background: alpha(theme.palette.background.paper, 0.7),
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Schedule color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Ihr Onboarding-Prozess
                        </Typography>
                        <Chip label="~11 Minuten" size="small" color="primary" variant="outlined" />
                    </Box>

                    <List sx={{ p: 0 }}>
                        {ONBOARDING_STEPS.map((step, index) => (
                            <ListItem
                                key={index}
                                sx={{
                                    px: 0,
                                    py: 1.5,
                                    borderRadius: 1,
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    },
                                }}
                            >
                                <ListItemIcon>
                                    <Avatar
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        {step.icon}
                                    </Avatar>
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {step.title}
                                        </Typography>
                                    }
                                    secondary={step.description}
                                />
                                <Chip label={step.duration} size="small" variant="outlined" sx={{ ml: 2 }} />
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>
        </Box>
    );
}
